
import { OpenInNew } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import type { Location } from '@/lib/types';
import type { MapEntityArea, MapEntityPoint } from '@common/schemas/entities/location.schema';
import { useNovel } from '@/context/novel-context';

interface MapEntityOverlayProps {
    location: Location;
    /** Natural dimensions of the rendered map image (after onLoad). */
    imageWidth: number;
    imageHeight: number;
    /** Rendered area of the image in the DOM (for offset/scale). */
    renderWidth: number;
    renderHeight: number;
    offsetLeft: number;
    offsetTop: number;
}

/** Convert map-space coordinates (0–1024) to a percentage of the image render area. */
function mapToPercent(val: number, total: number, mapAxis: number): string {
    return `${(val / mapAxis) * total}px`;
}

interface EntityCardProps {
    name: string;
    type: string;
    entityType: 'locations' | 'events';
    entityId: string;
    placement?: 'top' | 'bottom';
}

function EntityCard({ name, type, entityType, entityId, placement = 'top' }: EntityCardProps) {
    const route = entityType === 'locations'
        ? `/locations/view/${entityId}`
        : `/events/view/${entityId}`;

    const isTop = placement === 'top';

    return (
        <Paper
            elevation={8}
            sx={{
                position: 'absolute',
                ...(isTop ? {
                    bottom: 'calc(100% + 8px)',
                } : {
                    top: 'calc(100% + 8px)',
                }),
                left: '50%',
                transform: 'translateX(-50%)',
                width: 180,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(18, 18, 18, 0.95)',
                border: '1px solid',
                borderColor: entityType === 'locations' ? 'primary.main' : 'secondary.main',
                backdropFilter: 'blur(8px)',
                zIndex: 20,
                pointerEvents: 'auto',
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    ...(isTop ? {
                        top: '100%',
                    } : {
                        bottom: '100%',
                    }),
                    left: 0,
                    right: 0,
                    height: '14px',
                    backgroundColor: 'transparent',
                },
            }}
        >
            <Typography variant="caption" color={entityType === 'locations' ? 'primary.light' : 'secondary.light'} fontWeight="bold" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 1 }}>
                {entityType === 'locations' ? 'Location' : 'Event'}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.3, lineHeight: 1.2 }}>
                {name}
            </Typography>
            {type && type !== 'locations' && type !== 'events' && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {type}
                </Typography>
            )}
            <Box mt={1}>
                <Button
                    component={Link}
                    to={route}
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
                    fullWidth
                    sx={{ fontSize: '0.65rem', py: 0.3, borderColor: 'rgba(255,255,255,0.2)' }}
                >
                    View
                </Button>
            </Box>
            {/* Caret */}
            <Box sx={{
                position: 'absolute',
                ...(isTop ? {
                    bottom: -6,
                    borderTop: `6px solid ${entityType === 'locations' ? '#1976d2' : '#9c27b0'}`,
                } : {
                    top: -6,
                    borderBottom: `6px solid ${entityType === 'locations' ? '#1976d2' : '#9c27b0'}`,
                }),
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0, height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
            }} />
        </Paper>
    );
}

export function MapEntityOverlay({
    location,
    imageWidth, imageHeight,
    renderWidth, renderHeight,
    offsetLeft, offsetTop,
}: MapEntityOverlayProps) {
    const [hoveredUid, setHoveredUid] = useState<string | null>(null);
    const leaveTimeout = useRef<number | null>(null);

    const { novel } = useNovel();
    const locationSummaries = novel?.summaries?.locations || {};
    const eventSummaries = novel?.summaries?.events || {};

    const getEntityName = (ref: { type: 'locations' | 'events', id: string, name: string }) => {
        if (ref.type === 'locations') {
            const sum = locationSummaries[ref.id];
            if (sum) return sum.name;
        } else if (ref.type === 'events') {
            const sum = eventSummaries[ref.id];
            if (sum) return sum.name;
        }
        return ref.name;
    };

    const handleMouseEnter = (uid: string) => {
        if (leaveTimeout.current) {
            clearTimeout(leaveTimeout.current);
            leaveTimeout.current = null;
        }
        setHoveredUid(uid);
    };

    const handleMouseLeave = () => {
        if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
        leaveTimeout.current = window.setTimeout(() => {
            setHoveredUid(null);
        }, 300);
    };

    const mapW = location.mapWidth ?? 1024;
    const mapH = location.mapHeight ?? 1024;

    const points: MapEntityPoint[] = location.mapEntityPoints ?? [];
    const areas: MapEntityArea[] = location.mapEntityAreas ?? [];

    if (points.length === 0 && areas.length === 0) return null;

    const toScreenX = (x: number) => offsetLeft + (x / mapW) * renderWidth;
    const toScreenY = (y: number) => offsetTop + (y / mapH) * renderHeight;

    return (
        <Box
            sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}
        >
            {/* SVG layer for areas */}
            <svg
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}
                preserveAspectRatio="none"
            >
                {areas.map(area => {
                    if (area.vertices.length < 3) return null;
                    const pts = area.vertices.map(v => `${toScreenX(v.x)},${toScreenY(v.y)}`).join(' ');
                    const cx = area.vertices.reduce((s, v) => s + v.x, 0) / area.vertices.length;
                    const cy = area.vertices.reduce((s, v) => s + v.y, 0) / area.vertices.length;
                    const isHovered = hoveredUid === area.uid;

                    return (
                        <g key={area.uid}
                            onMouseEnter={() => handleMouseEnter(area.uid)}
                            onMouseLeave={handleMouseLeave}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            <polygon
                                points={pts}
                                fill={area.style.fillColor}
                                fillOpacity={isHovered ? Math.min(area.style.fillOpacity * 2.5, 0.7) : area.style.fillOpacity}
                                stroke={area.style.borderColor}
                                strokeWidth={area.style.borderWidth}
                                strokeDasharray={area.style.dashPattern.join(',')}
                            />
                            <text
                                x={toScreenX(cx)}
                                y={toScreenY(cy)}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={14}
                                fontWeight="bold"
                                fill={area.style.borderColor}
                                style={{ pointerEvents: 'none', userSelect: 'none', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}
                            >
                                {getEntityName(area.entityRef)}
                            </text>
                        </g>
                    );
                })}
            </svg>

            {/* HTML pins for points */}
            {points.map(pt => {
                const sx = toScreenX(pt.x);
                const sy = toScreenY(pt.y);
                const label = pt.label || getEntityName(pt.entityRef);
                const isHovered = hoveredUid === pt.uid;
                const pinColor = pt.entityRef.type === 'locations' ? '#1976d2' : '#9c27b0';

                return (
                    <Box
                        key={pt.uid}
                        onMouseEnter={() => handleMouseEnter(pt.uid)}
                        onMouseLeave={handleMouseLeave}
                        sx={{
                            position: 'absolute',
                            left: sx,
                            top: sy,
                            transform: 'translate(-50%, -100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                            zIndex: isHovered ? 15 : 10,
                            filter: isHovered ? `drop-shadow(0 0 8px ${pinColor})` : 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
                            transition: 'filter 0.2s, transform 0.15s',
                            '&:hover': { transform: 'translate(-50%, -108%)' },
                        }}
                    >
                        {isHovered && (
                            <EntityCard
                                name={label}
                                type={pt.entityRef.type}
                                entityType={pt.entityRef.type}
                                entityId={pt.entityRef.id}
                                placement={sy < 110 ? 'bottom' : 'top'}
                            />
                        )}
                        <Box
                            sx={{
                                px: 1, py: 0.3,
                                bgcolor: pinColor,
                                color: 'white',
                                borderRadius: '10px',
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                mb: 0.25,
                            }}
                        >
                            {label}
                        </Box>
                        <Box sx={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${pinColor}` }} />
                    </Box>
                );
            })}

            {/* Area hover card (shown for hovered area) */}
            {areas.map(area => {
                if (hoveredUid !== area.uid || area.vertices.length < 3) return null;
                const cx = area.vertices.reduce((s, v) => s + v.x, 0) / area.vertices.length;
                const minY = Math.min(...area.vertices.map(v => v.y));
                const sx = toScreenX(cx);
                const sy = toScreenY(minY);
                return (
                    <Box
                        key={`card-${area.uid}`}
                        sx={{ position: 'absolute', left: sx, top: sy, transform: 'translate(-50%, -50%)', pointerEvents: 'auto', zIndex: 15 }}
                        onMouseEnter={() => handleMouseEnter(area.uid)}
                        onMouseLeave={handleMouseLeave}
                    >
                        <EntityCard
                            name={getEntityName(area.entityRef)}
                            type="Location"
                            entityType="locations"
                            entityId={area.entityRef.id}
                            placement="top"
                        />
                    </Box>
                );
            })}
        </Box>
    );
}
